/**
 * TIP Shell — Bridge protocol (transport layer).
 *
 * The APP-LEVEL message shapes (tip:init / tip:renderName / tip:slotRect / tip:broker /
 * tip:resolve / tip:resolved / tip:brokerResult / tip:input) are the SHARED
 * contract — imported from `shared/`, never redefined here. See SCAFFOLD-SPEC §4.
 *
 * This module adds only the TRANSPORT envelope the spike did not have:
 *   1. `FramePortHandshake` — the iframe-initiated MessageChannel port transfer
 *      (SCAFFOLD §4: "the SDK bootstrap creates a MessageChannel, posts one port
 *      to the parent"). NOTE: this shape is NOT yet in shared/; the SANDBOX SDK
 *      must speak it too, so it should be promoted to shared/ (see report).
 *   2. `ShellToFrame` — the Shell→frame superset. shared/types.ts currently files
 *      `tip:input` under BridgeFromFrame, but per SCAFFOLD §4 + the spike it is a
 *      Shell→frame forwarded-keystroke message. We reconcile locally and flag it.
 */
import type {
  BridgeToFrame,
  BridgeFromFrame,
  FrameRuntimeReport,
  Tier,
  IdentitySlot,
  BrokerMethod,
  SlotRect,
} from '../shared/index.js';

export type { BridgeToFrame, BridgeFromFrame, FrameRuntimeReport, Tier, IdentitySlot, BrokerMethod, SlotRect };

/**
 * Transport-level handshake envelope. The frame's SDK bootstrap posts this to the
 * parent via `window.parent.postMessage(msg, '*', [port2])`; the transferred
 * MessagePort rides in `event.ports[0]`. The `nonce` is the per-launch value the
 * Shell minted and passed into the frame URL; the frame echoes it back so the
 * Shell can bind the port to THIS launch (in addition to `event.source ===
 * iframe.contentWindow`).
 */
export interface FramePortHandshake {
  type: 'tip:port';
  nonce: string;
}

/**
 * Shell→frame superset: the shared BridgeToFrame union PLUS `tip:input`
 * (forwarded keystrokes, standard tier only). See the note above re: shared/.
 */
export type ShellToFrame = BridgeToFrame | { type: 'tip:input'; value: string };

/** The only broker methods the Shell will proxy (Door 1, §7). */
export const BROKER_METHODS: ReadonlySet<BrokerMethod> = new Set<BrokerMethod>([
  'getMyToken',
  'writeScore',
  'getScores',
]);
